<!-- eslint-disable vue-a11y/form-has-label -->
<template>
  <div
    ref="calendar"
    class="sam-calendar"
    data-samora
  ></div>
</template>

<script>
import flatpickr from 'flatpickr';
import 'flatpickr/dist/themes/material_blue.css';

export const DATEPICKER_MODES = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
  RANGE: 'range',
};

function singleModeHandler([date]) {
  return date ? new Date(date) : null;
}

function multipleModeHandler(datesArray) {
  return datesArray.map((date) => new Date(date));
}

function rangeModeHandler(datesArray) {
  // clone date array
  const [from, to] = datesArray.map((date) => new Date(date));

  // set range end date to end of day if only date
  if (to && this.onlyDate) to.setHours(23, 59, 59, 999);

  return [from, to];
}

const CHANGE_HANDLERS = {
  [DATEPICKER_MODES.SINGLE]: singleModeHandler,
  [DATEPICKER_MODES.MULTIPLE]: multipleModeHandler,
  [DATEPICKER_MODES.RANGE]: rangeModeHandler,
};

export default {
  name: 'SamCalendar',
  props: {
    /**
     * The Calendar value. Must be a JavaScript Date object.
     */
    value: {
      type: [Date, Array],
      required: false,
      default: null,
    },
    /**
     * The Calendar mode
     * @values single, range, multiple
     */
    mode: {
      type: String,
      required: false,
      default: DATEPICKER_MODES.SINGLE,
      validator(type) {
        return Object.values(DATEPICKER_MODES).includes(type);
      },
    },
    /**
     * If the Calendar should be for dates only
     * @values true, false
     */
    onlyDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Calendar should be for time only
     * @values true, false
     */
    onlyTime: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The Calendar MIN date
     */
    minDate: {
      type: Date,
      required: false,
      default: null,
    },
    /**
     * The Calendar MAX date
     */
    maxDate: {
      type: Date,
      required: false,
      default: null,
    },
    /**
     * the Calendar string separator when in multiple mode
     */
    multipleModeSeparator: {
      type: String,
      required: false,
      default: ', ',
    },
    /**
     * If the Calendar should disable it's native mobile picker (use non-native picker in mobile, when true)
     * @values true, false
     */
    disableMobile: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      flatpickrInstance: null,
    };
  },
  computed: {
    // wrapper for component model binding
    calendarValue: {
      get() {
        return this.value;
      },
      set(val) {
        const updatedValue = val ? this.updateDateWithMinMaxDates(val) : null;
        this.flatpickrInstance.setDate(updatedValue, false);
        this.$emit('input', updatedValue);
      },
    },
    areMinMaxDatesInvalid() {
      // checks if min and max dates are wrongly assigned
      return this.minDate && this.maxDate && this.minDate > this.maxDate;
    },
    validatedMinDate() {
      // if min date > max date it ignores min date prop to allow user selection
      if (this.areMinMaxDatesInvalid) {
        // eslint-disable-next-line no-console
        console.warn('SamCalendar :: Invalid min and max date range. Ignoring constraints');
        return null;
      }
      return this.minDate;
    },
    validatedMaxDate() {
      // if min date > max date it ignores max date prop to allow user selection
      if (this.areMinMaxDatesInvalid) {
        // eslint-disable-next-line no-console
        console.warn('SamCalendar :: Invalid min and max date range. Ignoring constraints');
        return null;
      }
      return this.maxDate;
    },
  },
  watch: {
    mode(newMode) {
      this.flatpickrInstance.set('mode', newMode);
    },
    minDate() {
      this.flatpickrInstance.set('minDate', this.validatedMinDate);
    },
    maxDate() {
      this.flatpickrInstance.set('maxDate', this.validatedMaxDate);
    },
    multipleModeSeparator(newMultipleModeSeparator) {
      this.flatpickrInstance.set('conjunction', newMultipleModeSeparator);
    },
    disableMobile(newDisableMobile) {
      this.flatpickrInstance.set('disableMobile', newDisableMobile);
    },
  },
  mounted() {
    this.initCalendar();
  },
  beforeDestroy() {
    this.flatpickrInstance.destroy();
  },
  methods: {
    initCalendar() {
      // see https://flatpickr.js.org/options/
      const config = {
        altInput: false,
        time_24hr: true,
        mode: this.mode,
        onChange: this.handleChange,
        conjunction: this.multipleModeSeparator,
        disableMobile: this.disableMobile,
        inline: true,
        noCalendar: this.onlyTime,
        enableTime: !this.onlyDate || this.onlyTime,
        minDate: this.validatedMinDate,
        maxDate: this.validatedMaxDate,
        defaultDate: this.updateDateWithMinMaxDates(this.value),
      };

      this.flatpickrInstance = flatpickr(this.$refs.calendar, config);
    },
    updateDateWithMinMaxDates(currVal) {
      // single mode should be an instance of Date
      if (currVal instanceof Date) return this.applyMinMaxDatesConstraint(currVal);
      // multiple or range mode should be an array of Date, else will return null
      return currVal?.map?.(this.applyMinMaxDatesConstraint) ?? null;
    },
    applyMinMaxDatesConstraint(date) {
      // return date if no required validation parameter is set
      if (!this.minDate && !this.maxDate) return date ? new Date(date) : null;

      // clone date to evaluate
      let validDate = new Date(date);

      // handle min date
      if (this.minDate && validDate < this.minDate) {
        validDate = new Date(this.minDate);
      }

      // handle max date
      if (this.maxDate && validDate > this.maxDate) {
        validDate = new Date(this.maxDate);
      }

      return validDate;
    },
    handleChange(datesArray) {
      // get change handler
      const handler = CHANGE_HANDLERS[this.mode];
      const payload = handler?.call?.(this, datesArray) ?? null;

      // set calendar value
      this.calendarValue = payload;

      /**
       * Triggers when Calendar changes its value
       * @event change
       * @property {Array<Date>} datesArray array of JS Dates.
       */
      this.$emit('change', payload);
    },
    clear() {
      this.flatpickrInstance.clear();
    },
  },
};
</script>

<style lang="scss">
@import '../../../scss/flatpickrUnbabelStyle.scss';
</style>
